import React, { useState } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  PenLine, LayoutDashboard, Calculator, FileText, Users,
  Briefcase, DollarSign, MessageSquare, LogOut, Menu, X, Lock, ArrowRight, UserPlus, Settings
} from "lucide-react";

const baseItems = [
  { to: "/app/dashboard", label: "Dashboard", Icon: LayoutDashboard, id: "nav-dashboard" },
  { to: "/app/calculadora", label: "Calculadora", Icon: Calculator, id: "nav-calculator" },
  { to: "/app/propostas", label: "Propostas", Icon: FileText, id: "nav-proposals" },
  { to: "/app/crm", label: "CRM", Icon: Users, id: "nav-crm" },
  { to: "/app/projetos", label: "Projetos", Icon: Briefcase, id: "nav-projects" },
  { to: "/app/financeiro", label: "Financeiro", Icon: DollarSign, id: "nav-finance" },
  { to: "/app/scripts", label: "Scripts", Icon: MessageSquare, id: "nav-scripts" },
  { to: "/app/equipe", label: "Equipe", Icon: UserPlus, id: "nav-team" },
  { to: "/app/configuracoes", label: "Configurações", Icon: Settings, id: "nav-settings" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const doLogout = () => { logout(); nav("/"); };

  const navItems = user?.role === "revisor" 
    ? [{ to: "/app/projetos", label: "Projetos", Icon: Briefcase, id: "nav-projects" }]
    : baseItems;

  // Bloqueio de Inadimplentes (SaaS)
  const isCheckout = location.pathname.startsWith("/app/checkout");
  if (user?.access_status === "inactive" && user?.role !== "admin" && !isCheckout) {

    return (
      <div className="min-h-screen bg-[#F6F5F0] flex flex-col items-center justify-center p-6 text-center">
        <Card className="max-w-md p-10 bg-white border border-black/10 rounded-none shadow-sm flex flex-col items-center">
          <div className="w-16 h-16 bg-red-100 flex items-center justify-center rounded-full mb-6">
            <Lock className="w-8 h-8 text-[#D9381E]" />
          </div>
          <h1 className="font-serif text-3xl mb-4">Acesso Restrito</h1>
          <p className="text-[#4A4A4A] mb-8 leading-relaxed">
            Sua conta foi criada com sucesso, mas o seu acesso ao painel principal ainda não foi liberado. 
            Por favor, realize o pagamento ou aguarde a aprovação do administrador.
          </p>
          <Link to="/app/checkout" className="w-full mb-3">
            <Button className="rounded-none bg-[#D9381E] text-white hover:bg-[#B82914] w-full py-6">
              Assinar Plano agora
            </Button>
          </Link>
          <Button variant="ghost" onClick={doLogout} className="rounded-none text-[#8A8A8A] hover:text-black w-full">
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>

        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F0] flex flex-col md:flex-row">
      {/* Mobile Topbar */}
      <div className="md:hidden bg-[#1A1A1A] text-white p-4 flex items-center justify-between z-40 border-b border-black">
        <div className="flex items-center gap-2">
          <PenLine className="w-5 h-5 text-[#D9381E]" strokeWidth={2.5} />
          <span className="font-serif text-xl font-bold">RevisaPro</span>
        </div>
        <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="p-2">
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Sidebar Overlay (Mobile) */}
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden" 
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed md:relative inset-y-0 left-0 z-50 w-64 bg-[#1A1A1A] text-[#F6F5F0] flex flex-col shrink-0 border-r border-black
        transform transition-transform duration-200 ease-in-out md:translate-x-0
        ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        <div className="hidden md:flex items-center gap-2 p-6 border-b border-white/10">
          <PenLine className="w-5 h-5 text-[#D9381E]" strokeWidth={2.5} />
          <span className="font-serif text-2xl font-bold">RevisaPro</span>
        </div>
        
        <nav className="flex-1 py-6 overflow-y-auto">
          {navItems.map(({ to, label, Icon, id }) => (
            <NavLink
              key={to} to={to} data-testid={id}
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2 ${
                  isActive
                    ? "border-[#D9381E] bg-white/5 text-white"
                    : "border-transparent text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
            >
              <Icon className="w-4 h-4" strokeWidth={1.8} />
              {label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/app/admin" data-testid="nav-admin"
              onClick={() => setIsMobileMenuOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-3 text-sm transition-colors border-l-2 mt-4 ${
                  isActive
                    ? "border-[#D9381E] bg-white/5 text-[#D9381E]"
                    : "border-transparent text-[#D9381E]/70 hover:text-[#D9381E] hover:bg-[#D9381E]/10"
                }`
              }
            >
              <Users className="w-4 h-4" strokeWidth={1.8} />
              Super Admin
            </NavLink>
          )}
        </nav>
        
        {user?.role !== "admin" && user?.role !== "revisor" && user?.plan !== "premium" && (
          <div className="px-4 mb-4">
            <Link to="/app/checkout?plan=pro">
              <Card className="p-4 bg-gradient-to-br from-[#D9381E] to-[#B82914] border-none rounded-none text-white cursor-pointer hover:opacity-90 transition-opacity">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-80 mb-1">Upgrade</div>
                <div className="font-serif text-sm font-bold flex items-center justify-between">
                  Seja RevisaPro <ArrowRight className="w-3 h-3" />
                </div>
              </Card>
            </Link>
          </div>
        )}
        
        <div className="p-4 border-t border-white/10">

          <div className="px-2 py-3">
            <div className="font-mono text-xs uppercase tracking-widest text-white/50">Logado como</div>
            <div className="text-sm mt-1 truncate" data-testid="current-user-name">{user?.name}</div>
            <div className="text-xs text-white/50 truncate">{user?.email}</div>
          </div>
          <Button
            onClick={doLogout} data-testid="logout-btn"
            className="w-full rounded-none bg-transparent border border-white/20 hover:bg-white hover:text-[#1A1A1A] mt-2"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
